import type { Division, Team, OrganizationalPosition, PositionAssignment } from '../services/api';

export interface OrganizationPerson {
  id: string;
  name: string;
  photo?: string | null;
}

export interface OrganizationTeamMembership {
  id: string;
  teamId: string;
  userId: string;
  role: string;
  userName: string;
  userEmail?: string;
  userPhoto?: string | null;
}

export interface OrganizationPosition extends Omit<OrganizationalPosition, 'assignments'> {
  assignments: Array<PositionAssignment & { id: string }>;
}

export const isProposedRole = (role: { description?: string | null }) =>
  /^\s*Proposed role\./i.test(role.description || '');

export interface OrganizationSnapshot {
  schemaVersion: 1;
  capturedAt: string;
  company: { id: string; name: string };
  divisions: Division[];
  teams: Team[];
  positions: OrganizationPosition[];
  teamMemberships: OrganizationTeamMembership[];
  people: OrganizationPerson[];
}

export interface OrganizationWorkspace {
  snapshot: OrganizationSnapshot;
  revision: number;
  versionId: string | null;
}

export interface OrganizationVersion {
  id: string;
  revision: number;
  label: string | null;
  note?: string | null;
  createdAt: string;
  actorName: string | null;
  summary: string;
  snapshot?: OrganizationSnapshot;
}

export interface OrganizationVersionDetail extends OrganizationVersion {
  snapshot: OrganizationSnapshot;
}

export interface OrganizationVersionPage {
  versions: OrganizationVersion[];
  nextCursor: string | null;
}

export interface ProposalDivision {
  id: string;
  name: string;
  type: Division['type'];
  description?: string;
  parentDivisionId?: string | null;
}

export interface ProposalTeam {
  id: string;
  name: string;
  type: Team['type'];
  description?: string;
  divisionId?: string | null;
  parentTeamId?: string | null;
}

export interface ProposalPosition {
  id: string;
  title: string;
  description?: string;
  department?: string;
  parentPositionId?: string | null;
  divisionId?: string | null;
  teamId?: string | null;
}

export interface StructureProposal {
  divisions: ProposalDivision[];
  teams: ProposalTeam[];
  positions: ProposalPosition[];
}

export interface OrganizationalGenerationAnswers {
  primaryFocus: string;
  companyStage: string;
  functionalAreas: string[];
  geographicalScope: string;
  projectBasedWork: boolean;
  regulatoryCompliance: boolean;
  customerSegments: string[];
  additionalContext: string;
}

export interface StructurePreview {
  baseRevision: number;
  baseContextRevision?: number;
  proposal: StructureProposal;
  reasoning: string;
  recommendations: string[];
}

export interface StructureApplyRequest {
  operationId: string;
  expectedRevision: number;
  expectedContextRevision?: number;
  proposal: StructureProposal;
}

export interface StructureApplyResult {
  versionId: string;
  revision: number;
  snapshot: OrganizationSnapshot;
}

export const proposalCount = (proposal: StructureProposal) =>
  proposal.divisions.length + proposal.teams.length + proposal.positions.length;
